import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Button from '../Button';
import Typography from '../Typography';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import ClockIcon from '../Icons/ClockIcon';
import CloseIcon from '../Icons/CloseIcon';
import TimePickerPopover from './TimePickerPopover';
import { formatStoredTimeValue } from './timePickerValue';
import './TimePickerInput.scss';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

export default function TimePickerInput({
  id,
  value = '',
  onChange,
  disabled = false,
  placeholder,
  error = false,
  defaultToCurrentTime = false,
  className = '',
  overlayClassName = '',
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const fieldId = id || 'ds-time-picker-input';

  const displayPlaceholder = placeholder ?? 'HH:mm';

  const displayText = useMemo(() => {
    const formatted = formatStoredTimeValue(value);
    return formatted || null;
  }, [value]);

  const handleApply = useCallback(
    (nextValue) => {
      onChange?.(nextValue);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      onChange?.('');
      setOpen(false);
    },
    [onChange]
  );

  const ariaLabel = intl.formatMessage(
    {
      id: 'client-connect-time-picker-aria',
      defaultMessage: 'Time: {value}. Open time picker',
    },
    { value: displayText?.trim() || displayPlaceholder }
  );

  const triggerClassName = cx(
    'ds-time-picker-input__trigger',
    error && 'ds-time-picker-input__trigger--error',
    open && 'ds-time-picker-input__trigger--open'
  );

  const triggerContent = (
    <>
      <ClockIcon size={20} aria-hidden />
      <Typography
        component="span"
        variant="bodyMedium"
        className={cx(
          'ds-time-picker-input__trigger-text',
          !displayText && 'ds-time-picker-input__trigger-text--placeholder'
        )}
      >
        {displayText ?? displayPlaceholder}
      </Typography>
      <ChevronDownIcon
        size={16}
        className={cx('ds-time-picker-input__chevron', open && 'ds-time-picker-input__chevron--open')}
        aria-hidden
      />
    </>
  );

  return (
    <div className={cx('ds-time-picker-input', open && 'ds-time-picker-input--open', className)}>
      <div
        ref={anchorRef}
        className={cx(
          'ds-time-picker-input__shell',
          error && 'ds-time-picker-input__shell--error',
          displayText && !disabled && 'ds-time-picker-input__shell--has-value'
        )}
      >
        {disabled ? (
          <div
            className={cx('ds-time-picker-input__trigger', 'ds-time-picker-input__trigger--disabled')}
            aria-disabled="true"
            role="button"
            tabIndex={-1}
          >
            {triggerContent}
          </div>
        ) : (
          <Button
            id={`${fieldId}-trigger`}
            variant="white"
            size="base"
            fullWidth
            className={triggerClassName}
            onClick={() => setOpen(true)}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-invalid={error || undefined}
          >
            {triggerContent}
          </Button>
        )}
        {displayText && !disabled ? (
          <button
            type="button"
            className="ds-time-picker-input__clear"
            aria-label={intl.formatMessage({
              id: 'ds-time-picker-clear',
              defaultMessage: 'Clear time',
            })}
            onClick={handleClear}
          >
            <CloseIcon size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      <TimePickerPopover
        open={open}
        onDismiss={() => setOpen(false)}
        onApply={handleApply}
        onClear={displayText ? handleClear : undefined}
        value={value}
        defaultToCurrentTime={defaultToCurrentTime}
        anchorRef={anchorRef}
        overlayClassName={overlayClassName}
      />
    </div>
  );
}

TimePickerInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  defaultToCurrentTime: PropTypes.bool,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
};

TimePickerInput.defaultProps = {
  id: undefined,
  value: '',
  onChange: undefined,
  disabled: false,
  placeholder: undefined,
  error: false,
  defaultToCurrentTime: false,
  className: '',
  overlayClassName: '',
};
